import { useState } from 'react'
import { Alert, Button, Dialog, Spinner, Text, VStack } from '@chakra-ui/react'
import { FiTrash2 } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '../components/shared/PageContainer'
import { ResponsiveModal } from '../components/shared/ResponsiveModal'
import { ResponsiveTable } from '../components/shared/ResponsiveTable'
import { useDeleteReportRun, useReportRuns } from '../hooks/useReports'
import { extractApiMessage } from '../lib/apiError'
import type { ReportRunSummary } from '../types/reporting'

export function ReportRunsPage() {
  const { data, isLoading, error } = useReportRuns()
  const remove = useDeleteReportRun()
  const navigate = useNavigate()

  // Holding the target run (not just its id) lets the confirmation dialog name it without a
  // second lookup, and doubles as the dialog's open flag (`!!deleteTarget`) — same shape as
  // ListsPage/BusinessListPage/CampaignListPage's delete flow.
  const [deleteTarget, setDeleteTarget] = useState<ReportRunSummary | null>(null)

  const closeDialog = () => setDeleteTarget(null)

  const confirmDelete = () => {
    if (!deleteTarget) return
    // mutate, not mutateAsync: only close the dialog on actual success so a failed delete leaves
    // the confirmation open with its error visible, rather than silently discarding the attempt.
    remove.mutate(deleteTarget.id, { onSuccess: closeDialog })
  }

  if (isLoading) {
    return (
      <PageContainer title="Saved runs">
        <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
      </PageContainer>
    )
  }
  if (error) {
    return (
      <PageContainer title="Saved runs">
        <Text color="red.600">Could not load saved runs.</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer title="Saved runs" subtitle="Snapshots generated by your committee">
      <ResponsiveTable<ReportRunSummary>
        columns={[
          { header: 'Report', mobileRole: 'title', cell: (r) => r.title },
          { header: 'Generated by', mobileRole: 'meta', cell: (r) => r.generatedByEmail },
          { header: 'Generated', mobileRole: 'meta', cell: (r) => new Date(r.generatedAt).toLocaleString() },
        ]}
        rows={data ?? []}
        keyFn={(r) => r.id}
        onRowClick={(r) => navigate(`/dashboard/reports/runs/${r.id}`)}
        rowActions={[
          { label: 'Delete', icon: FiTrash2, color: 'red.500', onClick: (r) => setDeleteTarget(r) },
        ]}
        emptyMessage="No saved runs yet — open a report and generate a snapshot."
      />

      <ResponsiveModal isOpen={!!deleteTarget} onClose={closeDialog}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content role="alertdialog">
            <Dialog.Header fontSize="lg" fontWeight="bold">
              Delete saved run
            </Dialog.Header>
            <Dialog.Body>
              <Text>
                Are you sure you want to delete <strong>{deleteTarget?.title}</strong>? This
                snapshot cannot be recovered once deleted.
              </Text>
              {!!remove.error && (
                <Alert.Root status="error" mt={3}>
                  <Alert.Indicator />
                  <Alert.Content>{extractApiMessage(remove.error, 'Could not delete this run.')}</Alert.Content>
                </Alert.Root>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button onClick={closeDialog} disabled={remove.isPending}>Cancel</Button>
              <Button colorPalette="red" onClick={confirmDelete} ml={3} loading={remove.isPending}>
                Delete
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </PageContainer>
  )
}
